import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { FIELD_DEFAULTS, PALETTES } from '../theme';

// The shaders write final sRGB values directly and the palette was tuned
// against three r128, which had no color management. Opting out keeps the
// clear color and ink exactly as authored instead of double-converting.
THREE.ColorManagement.enabled = false;

const VERT = `
  precision highp float;
  attribute vec3 position;
  uniform float uTime,uBreath,uBreathAmt,uContrast,uBaseSize,uDpr,uAMin,uAMax;
  uniform vec2 uRes;
  varying float vAlpha;
  // --- simplex noise (Ashima / Gustavson, MIT) ---
  vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec2 mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}
  vec3 permute(vec3 x){return mod289(((x*34.0)+1.0)*x);}
  float snoise(vec2 v){
    const vec4 C=vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
    vec2 i=floor(v+dot(v,C.yy));
    vec2 x0=v-i+dot(i,C.xx);
    vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);
    vec4 x12=x0.xyxy+C.xxzz; x12.xy-=i1;
    i=mod289(i);
    vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));
    vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);
    m=m*m; m=m*m;
    vec3 x=2.0*fract(p*C.www)-1.0;
    vec3 h=abs(x)-0.5;
    vec3 ox=floor(x+0.5);
    vec3 a0=x-ox;
    m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);
    vec3 g;
    g.x=a0.x*x0.x+h.x*x0.y;
    g.yz=a0.yz*x12.xz+h.yz*x12.yw;
    return 130.0*dot(m,g);
  }
  void main(){
    float x=position.x, y=position.y;
    // organic, non-periodic field: domain-warped fractal noise that slowly drifts
    vec2 q=vec2(x,y)*0.00155;
    vec2 drift=vec2(uTime*0.050,-uTime*0.034);
    vec2 warp=vec2(snoise(q+drift), snoise(q+vec2(5.2,1.3)-drift*0.8));
    vec2 qw=q+0.85*warp;
    float f=0.60*snoise(qw+drift)
           +0.30*snoise(qw*2.13-drift*1.3)
           +0.16*snoise(qw*4.37+drift*0.6);
    float n=f*0.5+0.5;
    n=0.5+(n-0.5)*uContrast;
    // breath: gentle brightness swell only, no zoom, no geometry moving
    n+=0.16*uBreathAmt*uBreath;
    n=clamp(n,0.0,1.0);
    vec2 p=vec2(x,y);
    float fx=min(p.x,uRes.x-p.x)/(uRes.x*0.16);
    float fy=min(p.y,uRes.y-p.y)/(uRes.y*0.16);
    float env=clamp(min(fx,fy),0.0,1.0); env=env*env*(3.0-2.0*env);
    vAlpha=(uAMin+(uAMax-uAMin)*n)*(0.32+0.68*env);
    vec2 clip=vec2((p.x/uRes.x)*2.0-1.0, 1.0-(p.y/uRes.y)*2.0);
    gl_Position=vec4(clip,0.0,1.0);
    float sz=uBaseSize*(0.35+0.9*n)*(1.0+0.08*uBreathAmt*uBreath);
    gl_PointSize=max(1.0, sz*uDpr);
  }`;

const FRAG = `
  precision highp float;
  uniform vec3 uInk;
  varying float vAlpha;
  void main(){
    vec2 pc=gl_PointCoord-vec2(0.5);
    float d=length(pc);
    if(d>0.5) discard;
    float edge=smoothstep(0.5,0.30,d);
    float a=vAlpha*edge;
    if(a<0.01) discard;
    gl_FragColor=vec4(uInk,a);
  }`;

/** Particle positions: a screen-covering grid, over-scanned past the edges. */
function buildGrid(density, W, H) {
    const sp = 8.5 - 5 * (density / 100); // 40% -> 6.5px, 84% -> 4.3px, 100% -> 3.5px
    const m = Math.max(sp * 4, 64);
    const cols = Math.ceil((W + 2 * m) / sp) + 1;
    const rows = Math.ceil((H + 2 * m) / sp) + 1;
    const arr = new Float32Array(cols * rows * 3);
    let idx = 0;
    for (let j = 0; j < rows; j++) {
        const y = -m + j * sp;
        for (let i = 0; i < cols; i++) {
            arr[idx++] = -m + i * sp;
            arr[idx++] = y;
            arr[idx++] = 0;
        }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    return geo;
}

export default function ParticleField({ theme = 'dark', dimmed = false, mobile = false, ...opts }) {
    const hostRef = useRef(null);
    // Latest tunables, read by the rAF loop without restarting it.
    const optsRef = useRef({ ...FIELD_DEFAULTS, ...opts, theme });
    optsRef.current = { ...FIELD_DEFAULTS, ...opts, theme };

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;

        // Own the canvas so a StrictMode remount always gets a fresh GL context.
        const canvas = document.createElement('canvas');
        canvas.style.cssText =
            'position:absolute;inset:0;width:100%;height:100%;display:block';
        host.appendChild(canvas);

        let renderer;
        try {
            renderer = new THREE.WebGLRenderer({
                canvas,
                antialias: true,
                alpha: false,
                preserveDrawingBuffer: true,
                powerPreference: 'high-performance',
            });
        } catch (e) {
            canvas.remove();
            return undefined;
        }
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

        const scene = new THREE.Scene();
        const cam = new THREE.Camera(); // clip space is computed in-shader
        const mat = new THREE.RawShaderMaterial({
            uniforms: {
                uTime: { value: 0 },
                uBreath: { value: 0 },
                uBreathAmt: { value: 0.55 },
                uContrast: { value: 1.4 },
                uBaseSize: { value: 2 },
                uDpr: { value: renderer.getPixelRatio() },
                uRes: { value: new THREE.Vector2(1, 1) },
                uAMin: { value: 0.11 },
                uAMax: { value: 0.86 },
                uInk: { value: new THREE.Color(0xf6f9fc) },
            },
            vertexShader: VERT,
            fragmentShader: FRAG,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            blending: THREE.NormalBlending,
        });
        const points = new THREE.Points(new THREE.BufferGeometry(), mat);
        points.frustumCulled = false;
        scene.add(points);

        let reduce = false;
        try {
            reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        } catch (e) {
            reduce = false;
        }

        let t = Math.random() * 20;      // noise time
        let bt = Math.random() * 6.2832; // breath phase
        let last = performance.now();
        let bg = null;
        let W = 0;
        let H = 0;
        let density = optsRef.current.density;
        let sizeRaf = 0;

        const applyUniforms = (breathVal) => {
            const o = optsRef.current;
            const pal = PALETTES[o.theme] || PALETTES.dark;
            const u = mat.uniforms;
            u.uTime.value = t;
            u.uBreath.value = breathVal;
            u.uBreathAmt.value = o.breath / 100;
            u.uContrast.value = 0.4 + (o.contrast / 100) * 1.7;
            u.uBaseSize.value = o.size;
            u.uAMin.value = pal.alpha.min;
            u.uAMax.value = pal.alpha.max;
            u.uInk.value.setRGB(...pal.ink);
            if (bg !== pal.bg) {
                renderer.setClearColor(pal.bg, 1);
                bg = pal.bg;
            }
        };

        const rebuild = () => {
            if (!W || !H) return;
            const geo = buildGrid(density, W, H);
            points.geometry.dispose();
            points.geometry = geo;
        };

        const resize = () => {
            const w = canvas.clientWidth || window.innerWidth || 0;
            const h = canvas.clientHeight || window.innerHeight || 0;
            if (w < 1 || h < 1) {
                // Laid out at zero, try again next frame rather than building an empty grid.
                cancelAnimationFrame(sizeRaf);
                sizeRaf = requestAnimationFrame(resize);
                return;
            }
            W = w;
            H = h;
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
            renderer.setSize(W, H, false);
            mat.uniforms.uRes.value.set(W, H);
            mat.uniforms.uDpr.value = renderer.getPixelRatio();
            rebuild();
            applyUniforms(0);
            renderer.render(scene, cam);
        };

        let raf = 0;
        const loop = () => {
            raf = requestAnimationFrame(loop);
            const now = performance.now();
            let dt = (now - last) / 1000;
            last = now;
            if (!(dt > 0)) dt = 0.016;
            if (dt > 0.05) dt = 0.05; // clamp so a hidden tab doesn't jump the field

            const o = optsRef.current;
            if (o.density !== density) {
                density = o.density;
                rebuild();
            }

            let sf = 0.16 + (o.speed / 100) * 0.95;
            if (reduce) sf *= 0.14;
            t += dt * sf;

            let bHz = 0.62; // calm, breath-paced glow
            if (reduce) bHz *= 0.5;
            bt += dt * bHz;

            applyUniforms(Math.sin(bt));
            renderer.render(scene, cam);
        };

        // A hidden tab pauses rAF; resync the clock so dt stays small on return.
        const onVis = () => {
            last = performance.now();
        };

        resize();
        let ro = null;
        if (window.ResizeObserver) {
            ro = new ResizeObserver(resize);
            ro.observe(canvas);
        }
        window.addEventListener('resize', resize);
        document.addEventListener('visibilitychange', onVis);
        raf = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(raf);
            cancelAnimationFrame(sizeRaf);
            window.removeEventListener('resize', resize);
            document.removeEventListener('visibilitychange', onVis);
            if (ro) ro.disconnect();
            points.geometry.dispose();
            mat.dispose();
            renderer.dispose();
            canvas.remove();
        };
    }, []);

    return (
        <div
            ref={hostRef}
            style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                opacity: mobile ? (dimmed ? 0.18 : 0.3) : dimmed ? 0.3 : 0.45,
                transition: 'opacity .7s ease',
            }}
        />
    );
}
